// src/pages/WorkerPublicView.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle, Handshake, Loader2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified base

const WorkerPublicView = () => {
  const { workerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [worker, setWorker] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const jobId = queryParams.get("jobId");
  const price = queryParams.get("price");
  const customerId = localStorage.getItem("customerId");

  /* =========================================================
     👤 Fetch Worker Profile
  ========================================================= */
  useEffect(() => {
    if (!workerId) return;
    const fetchWorker = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/worker/public-profile/${workerId}`);
        setWorker(res.data);
      } catch (err) {
        console.error("❌ Error fetching worker profile:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchWorker();
  }, [workerId]);

  /* =========================================================
     📦 Fetch Job Details (for accepted screen)
  ========================================================= */
  useEffect(() => {
    if (!jobId) return;
    const fetchJob = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/customers/jobs/jobs/${jobId}`);
        setJob(res.data.job || null);
      } catch (err) {
        console.error("❌ Error fetching job data:", err);
      }
    };
    fetchJob();
  }, [jobId]);

  /* =========================================================
     🤝 Confirm Hire
  ========================================================= */
  const handleConfirmHire = async () => {
    if (!jobId) return alert("❌ No job ID found.");
    setProcessing(true);
    try {
      await axios.post(`${API_BASE}/api/customers/jobs/accept-bid`, { jobId, workerId });

      navigate("/bid-accepted", {
        state: {
          price: price || job?.budget || "N/A",
          workerName: worker?.name || "Worker",
          jobTitle: job?.jobTitle || "Job",
          jobId: job?._id || jobId,
          scheduledAt: job?.scheduledAt || "TBD",
          location:
            `${job?.street || ""}, ${job?.city || ""}, ${job?.province || ""} ${job?.postalCode || ""}`.trim() ||
            job?.location ||
            "Not Provided",
          customerId: job?.customerId?._id || job?.customerId || customerId,
          assignedTo: workerId,
        },
      });
    } catch (err) {
      console.error("❌ Error accepting bid", err);
      alert("Failed to accept bid.");
    } finally {
      setProcessing(false);
      setConfirmOpen(false);
    }
  };

  /* =========================================================
     🌀 Loading States
  ========================================================= */
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600 text-lg">
        Loading worker profile...
      </div>
    );

  if (!worker)
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600 text-lg">
        Worker not found.
      </div>
    );

  const photoURL = `${API_BASE}${worker.profilePhotoUrl || "/uploads/default-profile.png"}`;
  const verified = worker.status === "approved";

  /* =========================================================
     🎨 UI
  ========================================================= */
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 py-10 px-4 md:px-12 pb-24 relative">
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl overflow-hidden border border-gray-200"
      >
        <div className="relative bg-gradient-to-r from-indigo-600 to-blue-500 h-40 flex items-end justify-center">
          <img
            src={photoURL}
            alt={worker.name}
            className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg absolute -bottom-12"
          />
        </div>

        <div className="pt-16 pb-8 text-center px-6">
          <h1 className="text-2xl font-bold text-gray-800 flex justify-center items-center gap-2">
            {worker.name || "Unnamed Worker"}
            {verified && <CheckCircle className="text-green-500 w-5 h-5" />}
          </h1>
          <p className="text-gray-500 text-sm">
            {worker.city || "Unknown City"}, {worker.province || "Unknown Province"}
          </p>
          <p className="mt-1 text-sm text-indigo-600 font-medium">
            {worker.tier ? worker.tier.toUpperCase() : "SILVER"} Tier
          </p>
        </div>
      </motion.div>

      {/* BODY */}
      <div className="max-w-4xl mx-auto mt-8 space-y-6">
        {/* About Me */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl shadow p-6"
        >
          <h2 className="text-lg font-semibold text-gray-800">🌟 About Me</h2>
          <p className="mt-2 text-gray-600 text-sm leading-relaxed">
            {worker.aboutMe || "This worker hasn’t shared anything yet."}
          </p>
        </motion.div>

        {/* Experience */}
        {worker.experience && (
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl shadow p-6"
          >
            <h2 className="text-lg font-semibold text-gray-800">🛠️ Experience & Skills</h2>
            <p className="mt-2 text-gray-600 text-sm leading-relaxed">{worker.experience}</p>
          </motion.div>
        )}

        {/* Services */}
        {worker.services?.length > 0 && (
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl shadow p-6"
          >
            <h2 className="text-lg font-semibold text-gray-800 mb-4">🏅 Services & Certifications</h2>
            <div className="flex flex-wrap gap-3">
              {worker.services.map((s, i) => (
                <span
                  key={i}
                  className="bg-indigo-100 text-indigo-800 text-sm px-4 py-2 rounded-full shadow-sm"
                >
                  {s.name}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* Portfolio */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl shadow p-6"
        >
          <h2 className="text-lg font-semibold text-gray-800 mb-3">📸 Work Showcase</h2>
          {worker.portfolio?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {worker.portfolio.map((img, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-xl shadow hover:shadow-lg transition"
                >
                  <img
                    src={`${API_BASE}${img.imageUrl}`}
                    alt={`${worker.name} work ${i + 1}`}
                    className="w-full h-36 object-cover rounded-xl"
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No portfolio uploaded.</p>
          )}
        </motion.div>

        {/* Stats */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="grid grid-cols-3 gap-3 mt-8 text-center"
        >
          <div className="bg-white/80 p-4 rounded-xl shadow border border-gray-200">
            <p className="text-green-600 text-xl font-bold">{worker.satisfactionRate || "100%"}</p>
            <p className="text-sm text-gray-600">Satisfaction</p>
          </div>
          <div className="bg-white/80 p-4 rounded-xl shadow border border-gray-200">
            <p className="text-red-500 text-xl font-bold">{worker.cancellationRate || "0%"}</p>
            <p className="text-sm text-gray-600">Cancellation</p>
          </div>
          <div className="bg-white/80 p-4 rounded-xl shadow border border-gray-200">
            <p className="text-blue-600 text-xl font-bold">{worker.acceptanceRate || "100%"}</p>
            <p className="text-sm text-gray-600">Acceptance</p>
          </div>
        </motion.div>
      </div>

      {/* HIRE BUTTON */}
      <div className="fixed bottom-4 left-0 right-0 flex justify-center">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setConfirmOpen(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white font-medium px-4 py-2 rounded-full shadow-md hover:shadow-indigo-500/30 text-sm transition-all"
        >
          <Handshake size={16} /> Hire This Worker
        </motion.button>
      </div>

      {/* CONFIRM MODAL */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 w-80 shadow-2xl text-center"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Confirm Hire</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to hire{" "}
              <span className="font-semibold">{worker.name}</span> for this job?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm rounded-full border border-gray-300 hover:bg-gray-100 transition"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmHire}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition flex items-center justify-center gap-2"
                disabled={processing}
              >
                {processing ? (
                  <>
                    <Loader2 className="animate-spin w-4 h-4" /> Hiring...
                  </>
                ) : (
                  "Yes, Hire"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* PROCESSING OVERLAY */}
      {processing && (
        <div className="fixed inset-0 bg-black/40 flex flex-col items-center justify-center z-50 text-white">
          <Loader2 className="animate-spin w-10 h-10 mb-3" />
          <p className="text-sm tracking-wide">Processing your request...</p>
        </div>
      )}
    </div>
  );
};

export default WorkerPublicView;
